import React from 'react';
import { MassDataComponentProps, MassDataLeasingOfferForTable } from './mass-data-component.types';
import SimpleTable from '../../../components/simple-table';
import { SimpleTableColumn, SimpleTableColumnTypes } from '../../../components/simple-table/simple-table.props';
import { Button, Flex, Text } from '../../../common/common-components.styled';
import Pagination from '../../../components/pagination';
import usePagination from '../../../hooks/use-pagination';
import useMassDataLeasingOffers from './hooks/use-mass-data-leasing-offers';
import { Section } from '../../existing-vehicle-page/existing-vehicle';
import { SectionHeader } from '../details-view';
import { getDateString } from '../../../utils/convert-UTC-date-to-local-date';
import { useSessionFilter } from '../../../hooks/use-session-filter';
import { PAGES } from '../../../common/enums';
import MassDataFilters from './mass-data-filters';
import { emptyFilterState } from './mass-data-filters/mass-data-filters';
import SpinnerContainer from '../../../components/spinner-container';
import CheckboxHeaderComponent from './checkbox-header-component';
import CheckboxRowComponent from './checkbox-row-component';
import { TrashIcon } from '../../../components/icons-new/trash';
import { useTheme } from 'styled-components';
import useDeactivateLeasingOffers from './hooks/use-deactivate-leasing-offers';
import useCheckedLeasingOffers from './hooks/use-checked-leasing-offers';

const MassDataComponent = ({ vehicleOfferId }: MassDataComponentProps) => {
  const theme = useTheme();
  const { pagination, pageChangeHandler } = usePagination();
  const { sessionFilter, setSessionFilter } = useSessionFilter(
    `${PAGES.COCKPIT_2_0}-mass-data`,
    emptyFilterState,
    emptyFilterState
  );

  const { massDataLeasingOffers, isMassDataLeasingOffersLoading, massDataLeasingOffersIds } = useMassDataLeasingOffers({
    pagination,
    search: sessionFilter.filterForServer,
    sort: null,
    uds_bezugid: vehicleOfferId || ''
  });
  const { changeHeaderCheckboxHandler, changeRowCheckboxHandler, clearCheckedLeasingOffers, checkedLeasingOffers } =
    useCheckedLeasingOffers(massDataLeasingOffersIds, sessionFilter);
  const { openDeactivationModal, isLoading } = useDeactivateLeasingOffers();

  const columns: SimpleTableColumn<MassDataLeasingOfferForTable>[] = [
    {
      title: 'checkbox',
      type: SimpleTableColumnTypes.Custom,
      headerComponent: (
        <CheckboxHeaderComponent
          checkedLeasingOffers={checkedLeasingOffers}
          massDataLeasingOffersIds={massDataLeasingOffersIds}
          changeCheckboxHandler={changeHeaderCheckboxHandler}
        />
      ),
      rowComponent: row => (
        <CheckboxRowComponent
          row={row}
          checkedLeasingOffers={checkedLeasingOffers}
          changeCheckboxHandler={changeRowCheckboxHandler}
        />
      ),
      width: '5%'
    },
    {
      title: 'Leasinggesellschaft',
      type: SimpleTableColumnTypes.Text,
      propName: 'new_leasingbank',
      width: '20%'
    },
    {
      title: 'Laufzeit',
      type: SimpleTableColumnTypes.Text,
      propName: 'uds_laufzeit',
      width: '12%'
    },
    {
      title: 'Laufleistung',
      type: SimpleTableColumnTypes.Text,
      propName: 'uds_laufleistungpa',
      width: '12%'
    },
    {
      title: 'Angefragt am',
      type: SimpleTableColumnTypes.Text,
      propName: 'createdon',
      width: '12%'
    },
    {
      title: 'Angefragt von',
      type: SimpleTableColumnTypes.Text,
      propName: 'uds_portal_userid',
      width: '15%'
    },
    {
      title: 'Gültig von',
      type: SimpleTableColumnTypes.Text,
      propName: 'uds_validfrom',
      width: '12%'
    },
    {
      title: 'Gültig bis',
      type: SimpleTableColumnTypes.Text,
      propName: 'uds_validuntil',
      width: '10%'
    },
    {
      title: 'Status',
      type: SimpleTableColumnTypes.Text,
      propName: 'uds_angebotstatecode',
      width: '10%'
    }
  ];

  const rowsForTable: MassDataLeasingOfferForTable[] =
    massDataLeasingOffers?.items.map(row => ({
      ...row,
      uds_laufzeit: row.uds_laufzeit ? `${row.uds_laufzeit} Monate` : '',
      uds_laufleistungpa: row.uds_laufleistungpa ? `${row.uds_laufleistungpa.toLocaleString('de-DE')} km` : '',
      uds_angebotstatecode: row.uds_angebotstatecode?.label || '',
      new_leasingbank: row.new_leasingbank?.name || '',
      uds_portal_userid: row.uds_portal_userid?.name || '',
      createdon: getDateString(row.createdon.value),
      uds_validfrom: getDateString(row.uds_validfrom?.value),
      uds_validuntil: getDateString(row.uds_validuntil?.value)
    })) || [];

  if (!massDataLeasingOffers?.items.length && Object.values(sessionFilter.filterForServer).every(item => !item)) {
    return null;
  }

  if (isMassDataLeasingOffersLoading) {
    return <SpinnerContainer minHeight='400px' />;
  }

  return (
    <Section id={'leasing-offers-table'}>
      <SectionHeader label='Massendaten' />
      <section>
        <MassDataFilters
          sessionFilter={sessionFilter}
          setSessionFilter={setSessionFilter}
          goToFirstPage={() => pageChangeHandler(1)}
          vehicleOfferId={vehicleOfferId}
        />

        {isLoading ? (
          <Flex justify='center' width='138px' bottom='35'>
            <SpinnerContainer minHeight='32px' style={{ width: '20px' }} />
          </Flex>
        ) : (
          <Button
            disabled={!checkedLeasingOffers.length}
            secondary
            bottom='35'
            onClick={() => openDeactivationModal(checkedLeasingOffers, clearCheckedLeasingOffers)}
          >
            <Flex>
              <TrashIcon
                color={!checkedLeasingOffers.length ? theme.colors.grey400 : theme.colors.blue}
                width={20}
                height={20}
              />
              <Text left='10'>Archivieren</Text>
            </Flex>
          </Button>
        )}

        <SimpleTable columns={columns} rows={rowsForTable} rowIdPropName='id' />

        {(massDataLeasingOffers?.count || 0) > pagination.number && (
          <Pagination
            activePage={pagination.start}
            itemsCountPerPage={pagination.number}
            totalItemsCount={massDataLeasingOffers?.count || 0}
            onPageChange={pageChangeHandler}
          />
        )}

        <Text top='40' color='grey600'>
          Es sind {massDataLeasingOffers?.count || 0} Datensätze vorhanden. / Es werden{' '}
          {massDataLeasingOffers?.items.length || 0} von {massDataLeasingOffers?.count || 0} Datensätzen angezeigt.
        </Text>
      </section>
    </Section>
  );
};

export default MassDataComponent;
